import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import PerfectScrollbar from 'perfect-scrollbar';
import styled from 'styled-components';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

type ScrollbarProps = WithClassname & {
  children: React.ReactNode;
  containerRef?: (container: HTMLElement) => void;
  onClick?: VoidFunction;
};

const StyledNativeScrollbar = styled.div`
  height: 100%;
  overflow: auto;
  width: 100%;
`;

export const Scrollbar = memo<ScrollbarProps>(({ children, ...props }) => {
  const Component = NativeScrollbar;

  return (
    <Component className={`scrollbar ${props.className || ''}`} {...props}>
      {children}
    </Component>
  );
});

export const NativeScrollbar = memo<ScrollbarProps>(
  ({ children, containerRef, ...props }) => {
    const scrollbarDOMRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (containerRef) {
        containerRef(scrollbarDOMRef.current as HTMLElement);
      }
    }, [containerRef]);

    return (
      <StyledNativeScrollbar {...props} ref={scrollbarDOMRef}>
        {children}
      </StyledNativeScrollbar>
    );
  },
);

type PerfectScrollbarRef = { updateScroll: VoidFunction };

export const VirtualScrollbar = memo<ScrollbarProps>(({ children, ...props }) => {
  const scrollbarRef = useRef<PerfectScrollbarRef | null>(null);

  useEffect(() => {
    if (scrollbarRef.current) {
      window.requestAnimationFrame(() => {
        scrollbarRef.current?.updateScroll();
      });
    }
  }, [scrollbarRef]);

  const handleRef = useCallback((ref: PerfectScrollbarRef | null) => {
    scrollbarRef.current = ref;
  }, []);

  return (
    <StyledReactPerfectScrollBar {...props} ref={handleRef}>
      {children}
    </StyledReactPerfectScrollBar>
  );
});

const handlerNameByEvent = {
  'ps-scroll-y': 'onScrollY',
  'ps-scroll-x': 'onScrollX',
  'ps-scroll-up': 'onScrollUp',
  'ps-scroll-down': 'onScrollDown',
  'ps-scroll-left': 'onScrollLeft',
  'ps-scroll-right': 'onScrollRight',
  'ps-y-reach-start': 'onYReachStart',
  'ps-y-reach-end': 'onYReachEnd',
  'ps-x-reach-start': 'onXReachStart',
  'ps-x-reach-end': 'onXReachEnd',
};
Object.freeze(handlerNameByEvent);

interface ReactPerfectScrollBarProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * class name on container
   */
  className?: string;

  /**
   * style on container
   */
  style?: React.CSSProperties;

  /**
   * perfect-scrollbar init options
   */
  options?: PerfectScrollbar.Options;

  /**
   * get the container ref
   */
  containerRef?: (container: HTMLElement) => void;

  /**
   * fires when the y-axis is scrolled in either direction.
   */
  onScrollY?: (container: HTMLElement) => void;

  /**
   * fires when the x-axis is scrolled in either direction.
   */
  onScrollX?: (container: HTMLElement) => void;

  /**
   * fires when scrolling upwards.
   */
  onScrollUp?: (container: HTMLElement) => void;

  /**
   * fires when scrolling downwards.
   */
  onScrollDown?: (container: HTMLElement) => void;

  /**
   * fires when scrolling to the left.
   */
  onScrollLeft?: (container: HTMLElement) => void;

  /**
   * fires when scrolling to the right.
   */
  onScrollRight?: (container: HTMLElement) => void;

  /**
   * fires when scrolling reaches the start of the y-axis.
   */
  onYReachStart?: (container: HTMLElement) => void;

  /**
   * fires when scrolling reaches the end of the y-axis (useful for infinite scroll).
   */
  onYReachEnd?: (container: HTMLElement) => void;

  /**
   * fires when scrolling reaches the start of the x-axis.
   */
  onXReachStart?: (container: HTMLElement) => void;

  /**
   * fires when scrolling reaches the end of the x-axis.
   */
  onXReachEnd?: (container: HTMLElement) => void;

  /**
   * component name
   */
  component?: string;

  /**
   * fires when the perfect-scrollbar is synced
   */
  onSync?: (ps: PerfectScrollbar) => void;
}

const ReactPerfectScrollBar = forwardRef<PerfectScrollbarRef, ReactPerfectScrollBarProps>(
  (props, ref) => {
    const {
      className = '',
      style,
      options,
      containerRef = () => {},
      onScrollY,
      onScrollX,
      onScrollUp,
      onScrollDown,
      onScrollLeft,
      onScrollRight,
      onYReachStart,
      onYReachEnd,
      onXReachStart,
      onXReachEnd,
      component = 'div',
      children,
      onSync,
      ...remainProps
    } = props;
    const Comp: any = component;

    const container = useRef<HTMLDivElement | null>(null);
    const _ps = useRef<PerfectScrollbar | null>(null);
    const handlerByEvent = useRef({});
    const onSyncInternal = useMemo(() => {
      if (props.onSync) {
        return props.onSync;
      }

      return (ps) => ps.update();
    }, [props.onSync]);

    const handleRef = useCallback(
      (ref) => {
        container.current = ref;
        containerRef?.(ref);
      },
      [containerRef],
    );

    const updateScroll = useCallback(() => {
      if (!_ps.current) {
        return;
      }

      onSyncInternal?.(_ps.current);
    }, [onSyncInternal]);

    const updateEventHook = useCallback((prevProps = {}, props = {}) => {
      Object.keys(handlerNameByEvent).forEach((key) => {
        const callback = props[handlerNameByEvent[key]];
        const prevCallback = prevProps[handlerNameByEvent[key]];

        if (callback !== prevCallback) {
          if (prevCallback) {
            const prevHandler = handlerByEvent.current[key];
            container.current?.removeEventListener(key, prevHandler, false);
            handlerByEvent.current[key] = null;
          }
          if (callback) {
            const handler = () => callback(container.current);
            container.current?.addEventListener(key, handler, false);
            handlerByEvent.current[key] = handler;
          }
        }
      });
    }, []);

    const updateClassName = useCallback(() => {
      const className = props.className;

      const psClassNames = container.current?.className
        .split(' ')
        .filter((name) => name.match(/^ps([-_].+|)$/))
        .join(' ');

      if (container.current) {
        container.current.className = `scrollbar-container${
          className ? ` ${className}` : ''
        }${psClassNames ? ` ${psClassNames}` : ''}`;
      }
    }, [props.className]);

    useEffect(() => {
      const options = props.options || {};

      // Prevent issue with horizontal scroll on Firefox because of decimal width
      if (options.scrollXMarginOffset === undefined) {
        options.scrollXMarginOffset = 1;
      }

      // Prevent issue with horizontal scroll on Firefox because of decimal height
      if (options.scrollYMarginOffset === undefined) {
        options.scrollYMarginOffset = 1;
      }

      if (!container.current) {
        return;
      }

      _ps.current = new PerfectScrollbar(container.current, options);

      if (typeof ref === 'function') {
        ref?.({
          updateScroll,
        });
      }

      // hook up events
      updateEventHook({}, {});
      updateClassName();

      return () => {
        Object.keys(handlerByEvent.current).forEach((key) => {
          const value = handlerByEvent.current[key];

          if (value) {
            container.current?.removeEventListener(key, value, false);
          }
        });
        handlerByEvent.current = {};
        _ps.current?.destroy();
      };
    }, [ref, updateScroll, updateClassName, updateEventHook, props.options]);

    useEffect(() => {
      updateEventHook(props, props);
      updateScroll();

      if (props.className !== className) {
        updateClassName();
      }
    }, [updateClassName, updateEventHook, className, props, updateScroll]);

    return (
      <Comp style={style} ref={handleRef} {...remainProps}>
        {children}
      </Comp>
    );
  },
);

const StyledReactPerfectScrollBar = styled(ReactPerfectScrollBar)`
  position: relative;
  height: 100%;

  .ps__rail-x:hover,
  .ps__rail-y:hover,
  .ps__rail-x:focus,
  .ps__rail-y:focus,
  .ps__rail-x.ps--clicking,
  .ps__rail-y.ps--clicking {
    background-color: rgba(233, 233, 233, 0.7);
    opacity: 1;
  }
`;
